import { useEffect, useMemo, useState } from "react";
import { bookingDepartmentFields, bookingDepartmentId, departmentSlot, pickedProvider } from "./bookingDepartment";

/*
 * The department field of a booking form (owner 10-28). Picking a provider (or
 * a date) pre-selects that provider's department by the booking rule
 * (bookingDepartmentId: Dr. Adupa -> CODE for visits on/after 2026-11-01, Dr.
 * Malik -> Frisco, everyone else -> the requested / staff department); staff
 * may change it, and the change holds until the provider or date changes.
 *
 *   ruleDepartment  the rule's department (what the form pre-selects)
 *   departmentId    what the form shows: the staff choice, else the rule
 *   overridden      staff chose a department other than the rule's
 *   picked          the staff choice as picked (null when untouched)
 *   setPicked(id)   the form's onChange
 *   fields(rule?)   the request fields ({departmentid[, department_override]});
 *                   `rule` lets a submit pass a freshly computed rule value.
 */
export function useBookingDepartment({ slots, selectedSlot, providers, providerId, visitDate, requested } = {}) {
  const [picked, setPicked] = useState(null);
  const providerKey = providerId === undefined || providerId === null ? "" : String(providerId);
  const dateKey = visitDate || "";

  // a new provider or date: back to the provider's own department
  useEffect(() => {
    setPicked(null);
  }, [providerKey, dateKey]);

  const ruleDepartment = useMemo(
    () => bookingDepartmentId({
      slot: departmentSlot(slots, selectedSlot),
      provider: pickedProvider(providers, providerId),
      providerId,
      visitDate,
      requested,
    }),
    [slots, selectedSlot, providers, providerId, visitDate, requested],
  );

  const current = bookingDepartmentFields({ picked, rule: ruleDepartment });
  return {
    ruleDepartment,
    departmentId: current.departmentid,
    overridden: Boolean(current.department_override),
    picked,
    setPicked,
    fields: (rule = ruleDepartment) => bookingDepartmentFields({ picked, rule }),
  };
}
